import {
    confirmPayslipImport,
    PayrollImportPreview,
    PayrollImportPreviewItem,
    PayrollImportResult,
    previewPayslipImport,
} from '@/lib/api/hr/hr-payroll-api'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    MdCheckCircle,
    MdClose,
    MdCloudUpload,
    MdError,
    MdHelpOutline,
    MdInsertDriveFile,
    MdPersonOff,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

/** Mesmo teto do endpoint: a leitura é síncrona e cada arquivo vai à IA. */
const MAX_IMPORT_FILES = 10

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatCompetence = (competence: string) => {
    const [year, month] = competence.split('-')
    return month && year ? `${month}/${year}` : competence
}

const formatCnpj = (cnpj?: string | null) =>
    cnpj && cnpj.length === 14
        ? cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        : cnpj || ''

const formatCpf = (cpf?: string | null) =>
    cpf && cpf.length === 11 ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : cpf || ''

/**
 * Decisão do operador para cada holerite:
 * número = funcionário escolhido; `null` = não é funcionário (descarta);
 * `undefined` = ainda não decidido.
 */
type Decisions = Record<number, number | null | undefined>

type Step = 'upload' | 'review' | 'result'

interface Props {
    open: boolean
    onClose: () => void
    /**
     * Chamado ao FECHAR, se algo foi gravado, para a lista de folhas recarregar.
     * Não pode ser chamado logo após gravar: a página troca tudo pelo spinner
     * enquanto recarrega, o modal desmonta e volta zerado no primeiro passo —
     * o operador nunca via o resultado.
     */
    onImported: () => void
}

export default function PayrollImportModal({ open, onClose, onImported }: Props) {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [step, setStep] = useState<Step>('upload')
    const [files, setFiles] = useState<File[]>([])
    const [reading, setReading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [preview, setPreview] = useState<PayrollImportPreview | null>(null)
    const [decisions, setDecisions] = useState<Decisions>({})
    const [result, setResult] = useState<PayrollImportResult | null>(null)
    const [imported, setImported] = useState(false)

    const busy = reading || saving

    useEffect(() => {
        if (open) {
            setStep('upload')
            setFiles([])
            setPreview(null)
            setDecisions({})
            setResult(null)
            setImported(false)
            dialogRef.current?.showModal()
        } else {
            dialogRef.current?.close()
        }
    }, [open])

    const handleClose = () => {
        if (busy) return
        onClose()
        if (imported) onImported()
    }

    const handleSelectFiles = (list: FileList | null) => {
        if (!list?.length) return

        /**
         * Copia a seleção AGORA, fora do updater: `input.files` é um FileList
         * **vivo**, e o `input.value = ''` que roda logo depois do change o
         * esvazia antes de o updater do `setState` executar.
         */
        const picked = Array.from(list)

        // Acumula em vez de substituir: o operador costuma escolher os holerites
        // em levas (uma pasta por departamento, um arquivo esquecido depois).
        setFiles(prev => {
            const merged = [...prev]
            picked.forEach(file => {
                if (!merged.some(f => f.name === file.name && f.size === file.size)) merged.push(file)
            })
            return merged.slice(0, MAX_IMPORT_FILES)
        })

        // Fora do updater: em StrictMode ele roda duas vezes e o toast duplicaria.
        if (files.length + picked.length > MAX_IMPORT_FILES) {
            toast.warning(
                `Máximo de ${MAX_IMPORT_FILES} arquivos por vez. Os excedentes foram descartados — envie-os em outra leva.`,
            )
        }
    }

    const handleRead = async () => {
        if (!files.length) {
            toast.warning('Selecione ao menos um arquivo')
            return
        }

        setReading(true)
        try {
            const res = await previewPayslipImport(files)
            if (!res.success || !res.result) {
                toast.error(res.error || 'Erro ao ler os holerites')
                return
            }

            const initial: Decisions = {}
            res.result.items.forEach(item => {
                if (item.match.status === 'MATCHED') initial[item.index] = item.match.employeeId ?? undefined
                // Quem não está no cadastro já nasce descartado: não é funcionário,
                // e a contabilidade já declarou o que precisava declarar.
                else if (item.match.status === 'NOT_FOUND') initial[item.index] = null
            })

            setPreview(res.result)
            setDecisions(initial)
            setStep('review')

            if (!res.result.items.length) {
                toast.warning('Nenhum holerite foi encontrado nos arquivos enviados')
            }
        } catch {
            toast.error('Erro ao ler os holerites')
        } finally {
            setReading(false)
        }
    }

    const items = preview?.items ?? []
    const pendingCount = items.filter(item => decisions[item.index] === undefined).length
    const confirmedCount = items.filter(item => typeof decisions[item.index] === 'number').length

    /** Dois holerites da mesma competência no mesmo funcionário: o segundo apagaria o primeiro. */
    const duplicatedIndexes = useMemo(() => {
        const byKey = new Map<string, number[]>()
        items.forEach(item => {
            const employeeId = decisions[item.index]
            if (typeof employeeId !== 'number') return
            const key = `${employeeId}|${item.competence}|${item.type}`
            byKey.set(key, [...(byKey.get(key) ?? []), item.index])
        })
        return new Set([...byKey.values()].filter(group => group.length > 1).flat())
    }, [items, decisions])

    const setDecision = (index: number, employeeId: number | null | undefined) => {
        setDecisions(prev => ({ ...prev, [index]: employeeId }))
    }

    const handleConfirm = async () => {
        if (!preview) return
        if (pendingCount > 0) {
            toast.warning('Confirme ou descarte todos os holerites antes de importar')
            return
        }
        if (duplicatedIndexes.size > 0) {
            toast.warning('O mesmo funcionário foi escolhido para mais de um holerite da mesma competência')
            return
        }

        setSaving(true)
        try {
            const res = await confirmPayslipImport({
                token: preview.token,
                decisions: items.map(item => ({
                    index: item.index,
                    employeeId: decisions[item.index] ?? null,
                })),
            })
            if (!res.success || !res.result) {
                toast.error(res.error || 'Erro ao importar os holerites')
                return
            }

            setResult(res.result)
            setStep('result')
            if (res.result.imported > 0) {
                toast.success(`${res.result.imported} holerite(s) importado(s)`)
                setImported(true)
            }
            if (res.result.failed > 0) {
                toast.warning(`${res.result.failed} holerite(s) não puderam ser gravados`)
            }
        } catch {
            toast.error('Erro ao importar os holerites')
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog ref={dialogRef} className="modal" onCancel={e => { e.preventDefault(); handleClose() }}>
            <div className="modal-box !max-w-4xl">
                <div className="flex items-start justify-between gap-4 mb-1">
                    <h3 className="font-bold text-lg">Importar Holerites da Contabilidade</h3>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={handleClose}
                        disabled={busy}
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <ul className="steps steps-horizontal w-full text-xs mb-5">
                    <li className="step step-secondary">Envio</li>
                    <li className={`step ${step !== 'upload' ? 'step-secondary' : ''}`}>Conferência</li>
                    <li className={`step ${step === 'result' ? 'step-secondary' : ''}`}>Resultado</li>
                </ul>

                {step === 'upload' && (
                    <UploadStep
                        files={files}
                        reading={reading}
                        fileInputRef={fileInputRef}
                        onSelect={handleSelectFiles}
                        onRemove={index => setFiles(prev => prev.filter((_, i) => i !== index))}
                    />
                )}

                {step === 'review' && preview && (
                    <ReviewStep
                        preview={preview}
                        decisions={decisions}
                        duplicatedIndexes={duplicatedIndexes}
                        disabled={saving}
                        onDecide={setDecision}
                    />
                )}

                {step === 'result' && result && <ResultStep result={result} />}

                <div className="modal-action">
                    {step === 'upload' && (
                        <>
                            <button className="btn btn-ghost" onClick={handleClose} disabled={busy}>
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleRead}
                                disabled={reading || files.length === 0}
                            >
                                {reading && <span className="loading loading-spinner loading-sm"></span>}
                                {reading ? 'Lendo holerites...' : 'Ler holerites'}
                            </button>
                        </>
                    )}

                    {step === 'review' && (
                        <>
                            <button
                                className="btn btn-ghost"
                                onClick={() => setStep('upload')}
                                disabled={saving}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={handleConfirm}
                                disabled={saving || pendingCount > 0 || confirmedCount === 0 || duplicatedIndexes.size > 0}
                            >
                                {saving && <span className="loading loading-spinner loading-sm"></span>}
                                {pendingCount > 0
                                    ? `${pendingCount} aguardando confirmação`
                                    : `Importar ${confirmedCount} holerite(s)`}
                            </button>
                        </>
                    )}

                    {step === 'result' && (
                        <button className="btn btn-secondary" onClick={handleClose}>
                            Fechar
                        </button>
                    )}
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={handleClose}>close</button>
            </form>
        </dialog>
    )
}

// ----------------------------------------------------------------------------
// Etapa 1: envio
// ----------------------------------------------------------------------------

function UploadStep({
    files,
    reading,
    fileInputRef,
    onSelect,
    onRemove,
}: {
    files: File[]
    reading: boolean
    fileInputRef: React.RefObject<HTMLInputElement | null>
    onSelect: (list: FileList | null) => void
    onRemove: (index: number) => void
}) {
    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm text-base-content/70">
                Envie os contracheques em PDF — o arquivo único da contabilidade com todos os
                funcionários ou um contracheque por pessoa. A inteligência artificial lê os valores e,
                antes de gravar qualquer coisa, você confere quem é cada funcionário.
            </p>

            <div className="alert alert-info py-2">
                <span className="text-xs">
                    A folha já foi calculada e declarada pela contabilidade: aqui só entram os valores a
                    pagar. Quem não for funcionário cadastrado não gera nada, e o sistema não cria
                    holerite no lugar do original — cada funcionário recebe por e-mail o PDF da
                    contabilidade quando a folha for aprovada.
                </span>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={e => {
                    onSelect(e.target.files)
                    e.target.value = ''
                }}
            />

            <button
                type="button"
                className="w-full border-2 border-dashed border-base-300 rounded-xl p-6 flex flex-col items-center gap-2 hover:border-secondary hover:bg-base-200/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                disabled={reading}
            >
                <MdCloudUpload className="text-4xl text-base-content/40" />
                <span className="font-medium text-base-content">Clique para selecionar os contracheques</span>
                <span className="text-xs text-base-content/60">
                    PDF, até 10 MB por arquivo e {MAX_IMPORT_FILES} arquivos por vez. A leitura pode levar
                    cerca de um minuto por arquivo.
                </span>
            </button>

            {files.length > 0 && (
                <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
                    {files.map((file, index) => (
                        <div
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-3 p-2 bg-base-200/50 rounded-lg"
                        >
                            <MdInsertDriveFile className="text-lg text-base-content/60 flex-shrink-0" />
                            <span className="flex-1 text-sm truncate">{file.name}</span>
                            <span className="text-xs text-base-content/50 flex-shrink-0">
                                {(file.size / 1024).toFixed(0)} KB
                            </span>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-circle"
                                onClick={() => onRemove(index)}
                                disabled={reading}
                            >
                                <MdClose />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ----------------------------------------------------------------------------
// Etapa 2: conferência
// ----------------------------------------------------------------------------

function ReviewStep({
    preview,
    decisions,
    duplicatedIndexes,
    disabled,
    onDecide,
}: {
    preview: PayrollImportPreview
    decisions: Decisions
    duplicatedIndexes: Set<number>
    disabled: boolean
    onDecide: (index: number, employeeId: number | null | undefined) => void
}) {
    const items = preview.items
    const matched = items.filter(item => typeof decisions[item.index] === 'number').length
    const pending = items.filter(item => decisions[item.index] === undefined).length
    const discarded = items.filter(item => decisions[item.index] === null).length
    const total = items
        .filter(item => typeof decisions[item.index] === 'number')
        .reduce((sum, item) => sum + item.netSalary, 0)

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SummaryTile label="Holerites lidos" value={String(items.length)} />
                <SummaryTile label="Serão importados" value={String(matched)} tone="success" />
                <SummaryTile label="Aguardando você" value={String(pending)} tone={pending ? 'warning' : undefined} />
                <SummaryTile label="Líquido a pagar" value={formatCurrency(total)} />
            </div>

            {discarded > 0 && (
                <p className="text-xs text-base-content/60">
                    {discarded} holerite(s) descartado(s): não são de funcionários cadastrados e não geram nada.
                </p>
            )}

            {preview.failures.length > 0 && (
                <div className="flex flex-col gap-2">
                    {preview.failures.map((failure, index) => (
                        <div key={`${failure.fileName}-${index}`} className="alert alert-error py-2">
                            <MdError className="text-lg" />
                            <div className="text-xs">
                                <p className="font-medium">{failure.fileName}</p>
                                <p>{failure.error}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-col gap-3 max-h-[55vh] overflow-y-auto pr-1">
                {items.map(item => (
                    <ReviewCard
                        key={item.index}
                        item={item}
                        employees={preview.employees}
                        decision={decisions[item.index]}
                        duplicated={duplicatedIndexes.has(item.index)}
                        disabled={disabled}
                        onDecide={employeeId => onDecide(item.index, employeeId)}
                    />
                ))}
            </div>
        </div>
    )
}

function SummaryTile({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'warning' }) {
    const toneClass = tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : 'text-base-content'
    return (
        <div className="rounded-xl border border-base-300/60 bg-base-200/40 px-3 py-2">
            <p className="text-xs text-base-content/60">{label}</p>
            <p className={`text-lg font-semibold ${toneClass}`}>{value}</p>
        </div>
    )
}

function ReviewCard({
    item,
    employees,
    decision,
    duplicated,
    disabled,
    onDecide,
}: {
    item: PayrollImportPreviewItem
    employees: PayrollImportPreview['employees']
    decision: number | null | undefined
    duplicated: boolean
    disabled: boolean
    onDecide: (employeeId: number | null | undefined) => void
}) {
    const [choosingOther, setChoosingOther] = useState(false)
    const candidates = item.match.candidates
    const chosen = typeof decision === 'number' ? employees.find(e => e.id === decision) : undefined
    const autoMatched = item.match.status === 'MATCHED' && decision === item.match.employeeId

    const borderClass = duplicated
        ? 'border-error'
        : decision === undefined
            ? 'border-warning'
            : decision === null
                ? 'border-base-300/60 opacity-70'
                : 'border-success/50'

    return (
        <div className={`rounded-xl border ${borderClass} bg-base-100 p-4`}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-4">
                {/* O que veio no documento */}
                <div className="flex flex-col gap-1 min-w-0">
                    <p className="text-xs uppercase tracking-wide text-base-content/50">No documento</p>
                    <p className="font-semibold truncate">{item.employeeName || 'Sem nome'}</p>
                    <p className="text-xs text-base-content/60">
                        {formatCompetence(item.competence)}
                        {item.employeeCpf ? ` · CPF ${formatCpf(item.employeeCpf)}` : ' · sem CPF'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
                        <span>Proventos <strong>{formatCurrency(item.totalEarnings)}</strong></span>
                        <span>Descontos <strong>{formatCurrency(item.totalDeductions)}</strong></span>
                        <span>Líquido <strong className="text-secondary">{formatCurrency(item.netSalary)}</strong></span>
                    </div>
                    <p className="text-xs text-base-content/60">
                        Empregador:{' '}
                        {item.employer.companyName
                            ? <strong>{item.employer.companyName}</strong>
                            : 'filial do cadastro'}
                        {item.employer.cnpj ? ` · CNPJ ${formatCnpj(item.employer.cnpj)}` : ''}
                    </p>
                    <p className="text-xs text-base-content/40 truncate mt-1">{item.fileName}</p>

                    {!item.hasOriginalDocument && (
                        <div className="flex items-start gap-1.5 text-xs text-warning mt-1">
                            <MdWarning className="flex-shrink-0 mt-0.5" />
                            <span>
                                Página dividida com outros funcionários: os valores entram na folha, mas não
                                há holerite individual para enviar por e-mail.
                            </span>
                        </div>
                    )}
                    {item.warnings.map(warning => (
                        <div key={warning} className="flex items-start gap-1.5 text-xs text-error mt-1">
                            <MdError className="flex-shrink-0 mt-0.5" />
                            <span>{warning}. Confira com o documento.</span>
                        </div>
                    ))}
                </div>

                {/* Quem é no cadastro */}
                <div className="flex flex-col gap-2 min-w-0">
                    <p className="text-xs uppercase tracking-wide text-base-content/50">No cadastro</p>

                    {autoMatched && chosen && !choosingOther && (
                        <div className="flex items-center gap-2 rounded-lg bg-success/10 px-3 py-2">
                            <MdCheckCircle className="text-success text-lg flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{chosen.name}</p>
                                <p className="text-xs text-base-content/60">
                                    {item.match.method === 'cpf' ? 'CPF igual ao do cadastro' : 'Nome idêntico ao do cadastro'}
                                    {chosen.companyName ? ` · ${chosen.companyName}` : ''}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                onClick={() => setChoosingOther(true)}
                                disabled={disabled}
                            >
                                Trocar
                            </button>
                        </div>
                    )}

                    {(!autoMatched || choosingOther) && (
                        <>
                            {candidates.length > 0 && item.match.status !== 'MATCHED' && (
                                <>
                                    <p className="text-xs text-base-content/70 flex items-center gap-1">
                                        <MdHelpOutline />
                                        Nome parecido — é esta pessoa?
                                    </p>
                                    <div className="flex flex-col gap-1.5">
                                        {candidates.map(candidate => {
                                            const selected = decision === candidate.employeeId
                                            return (
                                                <button
                                                    key={candidate.employeeId}
                                                    type="button"
                                                    className={`btn btn-sm justify-between h-auto min-h-9 py-1.5 normal-case ${selected ? 'btn-secondary' : 'btn-outline border-base-300'}`}
                                                    onClick={() => onDecide(selected ? undefined : candidate.employeeId)}
                                                    disabled={disabled}
                                                >
                                                    <span className="text-left min-w-0">
                                                        <span className="block truncate">{candidate.name}</span>
                                                        {candidate.companyName && (
                                                            <span className="block text-xs font-normal opacity-70 truncate">
                                                                {candidate.companyName}
                                                                {candidate.status !== 'ACTIVE' ? ' · desligado' : ''}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="text-xs font-normal opacity-70 flex-shrink-0">
                                                        {Math.round(candidate.score * 100)}%
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </>
                            )}

                            {item.match.status === 'NOT_FOUND' && decision === null && (
                                <div className="flex items-center gap-2 rounded-lg bg-base-200/60 px-3 py-2 text-sm">
                                    <MdPersonOff className="text-lg text-base-content/50 flex-shrink-0" />
                                    <span>Não encontrado no cadastro. Não será importado.</span>
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-xs">Outro funcionário do cadastro</span>
                                </label>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={
                                        typeof decision === 'number' && !candidates.some(c => c.employeeId === decision)
                                            ? String(decision)
                                            : ''
                                    }
                                    onChange={e => onDecide(e.target.value ? Number(e.target.value) : undefined)}
                                    disabled={disabled}
                                >
                                    <option value="">Selecione...</option>
                                    {employees.map(employee => (
                                        <option key={employee.id} value={employee.id}>
                                            {employee.name}
                                            {employee.companyName ? ` — ${employee.companyName}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className={`btn btn-xs ${decision === null ? 'btn-neutral' : 'btn-ghost'}`}
                                    onClick={() => onDecide(null)}
                                    disabled={disabled}
                                >
                                    <MdPersonOff />
                                    Não é funcionário
                                </button>
                                {item.match.status === 'MATCHED' && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => {
                                            onDecide(item.match.employeeId)
                                            setChoosingOther(false)
                                        }}
                                        disabled={disabled}
                                    >
                                        Voltar ao vínculo encontrado
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {chosen && item.employer.companyId && chosen.companyId !== item.employer.companyId && (
                        <div className="flex items-start gap-1.5 text-xs text-warning">
                            <MdWarning className="flex-shrink-0 mt-0.5" />
                            <span>
                                Registrado em <strong>{item.employer.companyName}</strong> pelo CNPJ do
                                holerite, mas cadastrado em <strong>{chosen.companyName || 'outra filial'}</strong>.
                                A folha será da {item.employer.companyName}; se o cadastro estiver errado,
                                corrija a filial do funcionário.
                            </span>
                        </div>
                    )}
                    {chosen && item.employer.cnpj && !item.employer.companyId && (
                        <div className="flex items-start gap-1.5 text-xs text-warning">
                            <MdWarning className="flex-shrink-0 mt-0.5" />
                            <span>
                                O CNPJ {formatCnpj(item.employer.cnpj)} do holerite não é de nenhuma filial da
                                organização. A folha será da filial do cadastro
                                ({chosen.companyName || 'sem filial'}).
                            </span>
                        </div>
                    )}

                    {duplicated && (
                        <p className="text-xs text-error">
                            Este funcionário já foi escolhido para outro holerite da mesma competência.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Etapa 3: resultado
// ----------------------------------------------------------------------------

function ResultStep({ result }: { result: PayrollImportResult }) {
    const withoutDocument = result.items.filter(item => item.success && !item.hasOriginalDocument).length

    return (
        <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">
                {result.imported} importado(s)
                {result.failed > 0 ? `, ${result.failed} com erro` : ''}
                {result.ignored > 0 ? `, ${result.ignored} descartado(s)` : ''}
            </p>

            {withoutDocument > 0 && (
                <div className="alert alert-warning py-2">
                    <span className="text-xs">
                        {withoutDocument} funcionário(s) ficaram sem holerite individual e não receberão e-mail
                        na aprovação.
                    </span>
                </div>
            )}

            <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
                {result.items.map((item, index) => (
                    <div
                        key={`${item.fileName}-${index}`}
                        className={`flex items-start gap-2 p-2 rounded-lg text-sm ${item.success ? 'bg-success/10' : 'bg-error/10'}`}
                    >
                        {item.success ? (
                            <MdCheckCircle className="text-lg text-success flex-shrink-0 mt-0.5" />
                        ) : (
                            <MdError className="text-lg text-error flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                            <p className="truncate font-medium">{item.employeeName || 'Funcionário'}</p>
                            <p className="text-xs text-base-content/70">
                                {item.success
                                    ? `${item.competence ? formatCompetence(item.competence) : ''}`
                                        + (item.companyName ? ` · ${item.companyName}` : '')
                                    : item.error}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
